// Router guard for a mode: the same registry and useFeature decision as the tabs. A hidden mode
// opened by URL renders "not enabled" (add-on off) or "not available" (role), never a blank.
import { Alert, AlertDescription, AlertTitle } from "@companyos/ui";
import type { ReactNode } from "react";
import { canOpen, modeAccess, openOnly, rulesByKey } from "../extensions/modes";
import { isManager } from "../features/company";
import { featureState, useCompany, useSession } from "../features/use-feature";
import { NoAccessPage, NotEnabledPage, NotFoundPage } from "../pages/status-pages";

/**
 * COS-MODES: a Setup page every member opens (their own settings, e.g. "Who may direct my
 * agents"): the `chart` gate's audience, not Setup's managers-only rule.
 */
export function MemberGate({ children }: { children: ReactNode }) {
  const { me } = useSession();
  const company = useCompany();
  if (!canOpen("chart", me, company)) return <NoAccessPage what="Settings" slug={company.slug} />;
  return <div data-mode-root="setup">{children}</div>;
}

export function ModeGate({
  modeKey,
  open = false,
  className,
  children,
}: {
  modeKey: string;
  /** A route of the mode open to its `openRoles` too (MEMBER-ENTRY: KPIs › My …). */
  open?: boolean;
  /** Classes for the mode's root (a full-height screen under it). */
  className?: string;
  children: ReactNode;
}) {
  const { me } = useSession();
  const company = useCompany();
  // A mode or one of its page gates (KPIS-MODE: `chart`, `cadence`, `report-weeks`, `finance`).
  const mode = rulesByKey(modeKey);
  if (!mode) return <NotFoundPage message={`There is no "${modeKey}" mode.`} />;

  const access = !open && openOnly(mode, company) ? "role" : modeAccess(mode, me, company);
  if (access === "feature") {
    return (
      <NotEnabledPage
        what={mode.label}
        feature={mode.feature}
        companyName={company.name}
        slug={company.slug}
      />
    );
  }
  if (access === "role") return <NoAccessPage what={mode.label} slug={company.slug} />;

  const feature = featureState(me, company.id, mode.feature);
  return (
    <div data-mode-root={mode.key} className={className}>
      {feature.readOnly ? (
        <Alert className="mb-4 border-nav-read-only bg-nav-read-only text-nav-read-only-foreground">
          <AlertTitle>{mode.label} is read-only</AlertTitle>
          <AlertDescription className="text-nav-read-only-foreground">
            The add-on is in its grace period: you can read and export, not change.
          </AlertDescription>
        </Alert>
      ) : null}
      {children}
    </div>
  );
}

/**
 * Data entry (hours, numbers, CSV import) lives in KPIs but is a managers' job, and hours and
 * import are `core`: it opens for owners, admins and agency operators whatever the `kpi` add-on,
 * so a company without the scorecard still records its hours. Numbers add their own `kpi` check.
 */
export function DataEntryGate({ what, children }: { what: string; children: ReactNode }) {
  const company = useCompany();
  if (!isManager(company)) return <NoAccessPage what={what} slug={company.slug} />;
  return <div data-mode-root="cadence">{children}</div>;
}
